/**
 * Screen 3's dates and times (M39-T11, spec §C.3.3). Africa/Accra is UTC+0
 * all year with no daylight saving, so a day and a time in Accra are read and
 * written in UTC, and every time sent to the server carries "+00:00".
 */

export const ACCRA_OFFSET = '+00:00'
const MINUTE = 60_000
const DAY = 24 * 60 * MINUTE

/** The server's limit on one availability request (M38-T08). */
export const MAX_RANGE_DAYS = 62

const pad = (n: number) => String(n).padStart(2, '0')

/** "YYYY-MM-DD" of a moment in Accra. */
export function dayOf(moment: Date): string {
  return moment.toISOString().slice(0, 10)
}

/** "HH:MM" of a moment in Accra. */
export function timeOf(moment: Date): string {
  return `${pad(moment.getUTCHours())}:${pad(moment.getUTCMinutes())}`
}

export function accraToday(now: Date): string {
  return dayOf(now)
}

export function addDays(day: string, n: number): string {
  return dayOf(new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY))
}

/** The moment of a day and "HH:MM" in Accra. */
export function at(day: string, time: string): Date {
  return new Date(`${day}T${time}:00${ACCRA_OFFSET}`)
}

/** ISO 8601 with Accra's offset, e.g. "2026-11-02T08:30:00+00:00". */
export function toIso(moment: Date): string {
  return `${dayOf(moment)}T${timeOf(moment)}:00${ACCRA_OFFSET}`
}

/** "Mon 2 Nov". */
export function dayLabel(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

/** Today and the next `maxDaysAhead` days: the days a booking may use. */
export function windowDays(today: string, maxDaysAhead: number): string[] {
  return Array.from({ length: maxDaysAhead + 1 }, (_, i) => addDays(today, i))
}

/** The window as [from, to] pieces the availability endpoint accepts. */
export function availabilityRanges(
  today: string,
  maxDaysAhead: number,
): Array<[string, string]> {
  const last = addDays(today, maxDaysAhead)
  const ranges: Array<[string, string]> = []
  let from = today
  while (from <= last) {
    const end = addDays(from, MAX_RANGE_DAYS - 1)
    const to = end < last ? end : last
    ranges.push([from, to])
    from = addDays(to, 1)
  }
  return ranges
}

/** "00:00" to "23:45". */
export function slots(stepMinutes = 15): string[] {
  return Array.from({ length: (24 * 60) / stepMinutes }, (_, i) => {
    const m = i * stepMinutes
    return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`
  })
}

/** Appointment − the area's travel allowance − the early arrival. */
export function suggestedPickup(
  appointment: Date,
  travelMinutes: number,
  earlyArrivalMinutes: number,
): Date {
  return new Date(
    appointment.getTime() - (travelMinutes + earlyArrivalMinutes) * MINUTE,
  )
}

/** Pickups "Change" may offer: 15-minute steps within ±`windowMinutes` of
 * the suggestion, and always before the appointment. */
export function pickupChoices(
  suggested: Date,
  windowMinutes: number,
  appointment: Date,
): Date[] {
  const out: Date[] = []
  for (let m = -windowMinutes; m <= windowMinutes; m += 15) {
    const choice = new Date(suggested.getTime() + m * MINUTE)
    if (choice < appointment) out.push(choice)
  }
  return out
}

/** "24 hours'", "1 hour's", "90 minutes'": the server's own wording (T04a). */
function noticeWords(minutes: number): string {
  if (minutes % 60 === 0) {
    const hours = minutes / 60
    return hours === 1 ? "1 hour's" : `${hours} hours'`
  }
  return `${minutes} minutes'`
}

/**
 * Why this pickup is too soon, worded as the checkout refuses it (M39-T04a),
 * or null. A missing notice setting refuses, as the server does.
 */
export function noticeProblem(
  pickup: Date,
  now: Date,
  minNoticeMinutes: number | null,
): string | null {
  if (pickup <= now) return 'That pickup time has passed. Choose a later time.'
  if (
    minNoticeMinutes === null ||
    pickup.getTime() < now.getTime() + minNoticeMinutes * MINUTE
  ) {
    const words =
      minNoticeMinutes === null ? 'more' : noticeWords(minNoticeMinutes)
    return `This service needs ${words} notice. Choose a later time or call the transport desk.`
  }
  return null
}
